import { ArrowUpDownIcon, TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import { BoxProps, Checkbox } from "@chakra-ui/react";
import React from "react";

import { ColumnDefinition, ColumnOrdering } from "./table";
import { TableCell } from "./table-cell";

export interface TableHeaderProps<TableRowType> extends BoxProps {
  columnDefinitions: ColumnDefinition<TableRowType>[];
  hasQuickRowAction?: boolean;
  hasRowActions?: boolean;
  hasSelection?: boolean;
  someChecked?: boolean;
  allChecked?: boolean;
  setAllChecked?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  ordering?: { [fieldName: string]: ColumnOrdering };
  setOrdering?: (fieldName: string, ordering: ColumnOrdering) => void;
  hasScrollbar?: boolean;
  themeColor: string;
}

export function TableHeader<TableRowType>(props: TableHeaderProps<TableRowType>) {
  const {
    columnDefinitions,
    hasQuickRowAction = false,
    hasRowActions = false,
    hasSelection = false,
    someChecked = false,
    allChecked = false,
    setAllChecked,
    ordering,
    setOrdering,
    hasScrollbar = false,
    themeColor,
    ...rest
  } = props;

  const onClickForColumn = (column: ColumnDefinition<TableRowType>): void => {
    if (!column.orderingField || !setOrdering) return;

    const current: ColumnOrdering = ordering ? ordering[column.orderingField] : undefined;

    if (!current) {
      setOrdering(column.orderingField, "az");
    } else if (current === "az") {
      setOrdering(column.orderingField, "za");
    } else {
      setOrdering(column.orderingField, undefined);
    }
  };

  return (
    <>
      {hasQuickRowAction ? (
        <TableCell
          key={`title-quickrowaction`}
          backgroundColor={themeColor}
          color="gray.100"
          {...rest}
        />
      ) : null}
      {hasSelection ? (
        <TableCell display="inline-flex" alignItems="center">
          <Checkbox
            size="lg"
            isChecked={allChecked}
            isIndeterminate={!allChecked && someChecked}
            onChange={setAllChecked}
            margin="0"
          />
        </TableCell>
      ) : null}
      {columnDefinitions.map((column, idx) => {
        const columnTitle =
          column.title ?? (column.titleRenderFunction && column.titleRenderFunction()) ?? "";

        return (
          <TableCell
            key={`title-${idx}`}
            backgroundColor={themeColor}
            color="gray.100"
            cursor={column.orderingField ? "pointer" : undefined}
            onClick={() => onClickForColumn(column)}
            title={typeof columnTitle === "string" ? columnTitle : undefined}
            textAlign={column.align ?? "left"}
          >
            {columnTitle}
            {column.orderingField && ordering ? (
              ordering[column.orderingField] === "az" ? (
                <TriangleDownIcon marginLeft="0.5rem" />
              ) : ordering[column.orderingField] === "za" ? (
                <TriangleUpIcon marginLeft="0.5rem" />
              ) : (
                <ArrowUpDownIcon marginLeft="0.5rem" />
              )
            ) : (
              ""
            )}
          </TableCell>
        );
      })}
      {hasRowActions ? (
        <TableCell
          key={`title-rowactions`}
          backgroundColor={themeColor}
          color="gray.100"
          {...rest}
        />
      ) : null}
      {hasScrollbar ? (
        <TableCell
          key={`title-scrollbar`}
          backgroundColor={themeColor}
          color="gray.100"
          padding={0}
          {...rest}
        />
      ) : null}
    </>
  );
}
